import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import Header from './Header';
import './App.css';

const API_BASE = window.location.origin;

function CoinDetails() {
  const { tokenmint } = useParams();
  const navigate = useNavigate();
  const [coinData, setCoinData] = useState(null);
  const [devData, setDevData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    loadCoinData();
  }, [tokenmint]);

  useEffect(() => {
    if (coinData && coinData.creator) {
      loadDevData(coinData.creator);
    }
  }, [coinData]);

  const loadCoinData = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Lade Coin-Daten aus dev-launches-data.json
      const response = await axios.get(`${API_BASE}/api/dev-launches/realtime?t=${Date.now()}`);
      const launches = response.data.data || response.data || [];
      
      const coin = launches.find(l => l.tokenmint === tokenmint);
      
      if (!coin) {
        setError('Coin not found');
        setLoading(false);
        return;
      }
      
      setCoinData(coin);
      setLoading(false);
    } catch (err) {
      console.error('[COIN DETAILS] Error loading coin data:', err);
      setError(err.message || 'Failed to load coin data');
      setLoading(false);
    }
  };

  const loadDevData = async (creator) => {
    try {
      const response = await axios.get(`${API_BASE}/api/dev/${encodeURIComponent(creator)}?t=${Date.now()}`);
      setDevData(response.data);
    } catch (err) {
      console.error('[COIN DETAILS] Error loading dev data:', err);
      // Dev-Daten sind optional, kein Fehler setzen
    }
  };

  const formatTimeAgo = (timestamp) => {
    if (!timestamp) return 'Unknown';
    const now = Date.now();
    const time = typeof timestamp === 'string' ? new Date(timestamp).getTime() : timestamp;
    const diff = now - time;
    
    const seconds = Math.floor(diff / 1000);
    const minutes = Math.floor(seconds / 60);
    const hours = Math.floor(minutes / 60);
    const days = Math.floor(hours / 24);
    
    if (days > 0) {
      return `${days} day${days > 1 ? 's' : ''} ${hours % 24} hour${(hours % 24) > 1 ? 's' : ''} ago`;
    } else if (hours > 0) {
      return `${hours} hour${hours > 1 ? 's' : ''} ${minutes % 60} min ago`;
    } else if (minutes > 0) {
      return `${minutes} min ago`;
    } else {
      return `${seconds} sec ago`;
    }
  };

  const formatNumber = (num) => {
    const value = parseFloat(num);
    if (isNaN(value)) return '0';
    if (value >= 1000000) {
      return (value / 1000000).toFixed(2) + 'M';
    } else if (value >= 1000) {
      return (value / 1000).toFixed(2) + 'K';
    }
    return value.toFixed(2);
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    // Optional: Toast notification
  };

  if (loading) {
    return (
      <div className="App">
        <Header />
        <div className="app-main">
          <div className="loading-container">
            <div className="loading-spinner"></div>
            <p>Loading coin data...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error || !coinData) {
    return (
      <div className="App">
        <Header />
        <div className="app-main">
          <div className="error-container">
            <h2>Error</h2>
            <p>{error || 'Coin not found'}</p>
            <button onClick={() => navigate('/')}>Back to Home</button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="App">
      <Header />
      <div className="app-main">
        <div className="coin-details-container">
          {/* Coin Header */}
          <div className="coin-details-header">
            <div className="coin-header-left">
              {coinData.imageurl && (
                <img 
                  src={coinData.imageurl} 
                  alt={coinData.name || coinData.symbol}
                  className="coin-details-image"
                  onError={(e) => { e.target.style.display = 'none'; }}
                />
              )}
              <div className="coin-header-info">
                <h1 className="coin-details-name">{coinData.symbol || coinData.name || 'Unknown'}</h1>
                {coinData.name && coinData.name !== coinData.symbol && (
                  <div className="coin-details-symbol">{coinData.name}</div>
                )}
                <div className="coin-details-tokenmint">
                  <span onClick={() => copyToClipboard(coinData.tokenmint)} style={{ cursor: 'pointer' }}>
                    {coinData.tokenmint}
                  </span>
                  <button onClick={() => copyToClipboard(coinData.tokenmint)} className="copy-btn-small">📋</button>
                </div>
                <div className="coin-details-time">
                  Created: {formatTimeAgo(coinData.created_at_timestamp || coinData.created_at)}
                </div>
              </div>
            </div>
            
            <div className="coin-header-right">
              <div className="coin-details-mc">
                <div className="coin-mc-label">Current Market Cap</div>
                <div className="coin-mc-value">{formatNumber(coinData.currentmarketcapsol_usd || coinData.currentmarketcapsol)}</div>
              </div>
              <div className="coin-details-ath">
                <div className="coin-ath-label">All-Time High</div>
                <div className="coin-ath-value">{formatNumber(coinData.peakmarketcapsol_usd || coinData.peakmarketcapsol)}</div>
              </div>
            </div>
          </div>

          {/* DEX Status */}
          <div className="coin-details-dex">
            <div className={`dex-badge ${coinData.dex_status === 'APPROVED' || coinData.dex_status === 'PAID' ? 'dex-paid' : 'dex-not-paid'}`}>
              {coinData.dex_status === 'APPROVED' || coinData.dex_status === 'PAID' ? 'DEX PAID' : 'DEX NOT PAID'}
              {coinData.dex_payment_time_ago && (
                <div className="dex-payment-time">{coinData.dex_payment_time_ago}</div>
              )}
            </div>
          </div>

          {/* Action Buttons */}
          <div className="coin-details-actions">
            <a 
              href={`https://pump.fun/coin/${coinData.tokenmint}`}
              target="_blank"
              rel="noopener noreferrer"
              className="action-btn-large pumpfun-btn-large"
            >
              <span className="action-btn-icon">🚀</span>
              <span className="action-btn-text">PUMP.FUN</span>
            </a>
            <a 
              href={`https://neo.bullx.io/terminal?chainId=1399811149&address=${coinData.tokenmint}`}
              target="_blank"
              rel="noopener noreferrer"
              className="action-btn-large bullx-btn-large"
            >
              <span className="action-btn-icon">📊</span>
              <span className="action-btn-text">BULLX</span>
            </a>
          </div>

          {/* Dev Info */}
          <div className="coin-details-dev">
            <div className="coin-dev-label">Developer</div>
            <div 
              className="coin-dev-wallet"
              onClick={() => navigate(`/dev/${encodeURIComponent(coinData.creator)}`)}
              style={{ cursor: 'pointer' }}
            >
              {coinData.creator}
            </div>
            {coinData.bonding_rate_percent && (
              <div className="coin-bonding-rate">
                Bonding Rate: {coinData.bonding_rate_percent}%
              </div>
            )}
          </div>

          {/* Dev Statistics Section */}
          {devData && devData.stats && (
            <div className="coin-dev-stats-section">
              <div className="dev-stats-card">
                <div className="dev-header">
                  <h2>Dev Statistics</h2>
                  <p className="dev-wallet-address">{coinData.creator}</p>
                </div>
                
                <div className="stats-grid">
                  <div className="stat-item">
                    <span className="stat-label">Total Launches</span>
                    <span className="stat-value highlight">{devData.stats.totallaunches || 0}</span>
                  </div>
                  <div className="stat-item">
                    <span className="stat-label">Bonded Count</span>
                    <span className="stat-value highlight">{devData.stats.bondedcount || 0}</span>
                  </div>
                  <div className="stat-item">
                    <span className="stat-label">Bonding Rate</span>
                    <span className="stat-value highlight neon-green">{devData.stats.bonding_rate_percent || '0.00'}%</span>
                  </div>
                  <div className="stat-item">
                    <span className="stat-label">Avg Peak MC (Not Bonded)</span>
                    <span className="stat-value highlight neon-gold">${formatNumber(devData.stats.avgpeakmarketcapnotbonded_usd || 0)}</span>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Last 10 Launches */}
          {devData && devData.coins && devData.coins.length > 0 && (
            <div className="coin-last-launches-section">
              <div className="last-launches-card">
                <div className="last-launches-header">
                  <h2>Last 10 Launches</h2>
                </div>
                
                <div className="last-launches-list">
                  {devData.coins.slice(0, 10).map((coin, index) => (
                    <div 
                      key={coin.tokenmint || index} 
                      className="last-launch-item"
                      onClick={() => navigate(`/coin/${coin.tokenmint}`)}
                      style={{ cursor: 'pointer' }}
                    >
                      <div className="last-launch-left">
                        {coin.imageurl && (
                          <img 
                            src={coin.imageurl} 
                            alt={coin.symbol || coin.name}
                            className="last-launch-image"
                            onError={(e) => { e.target.style.display = 'none'; }}
                          />
                        )}
                        <div className="last-launch-info">
                          <div className="last-launch-name">{coin.symbol || coin.name || 'Unknown'}</div>
                          <div className="last-launch-mint">{coin.tokenmint?.substring(0, 8)}...{coin.tokenmint?.substring(coin.tokenmint.length - 4)}</div>
                        </div>
                      </div>
                      <div className="last-launch-right">
                        <div className="last-launch-mc">
                          <span className="last-launch-mc-label">Current MC:</span>
                          <span className="last-launch-mc-value">${formatNumber(coin.currentmarketcapsol_usd || coin.currentmarketcapsol || 0)}</span>
                        </div>
                        <div className="last-launch-peak">
                          <span className="last-launch-peak-label">Peak MC:</span>
                          <span className="last-launch-peak-value">${formatNumber(coin.peakmarketcapsol_usd || coin.peakmarketcapsol || 0)}</span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default CoinDetails;
